import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { Stage, Layer, Image as KonvaImage, Line } from 'react-konva'

import { itemRemovalActions, FurnitureMaskLine } from '../../reducers/itemRemoval'
import { cursorStyle } from '../../utils/cursorStyles'
import { SquareLine, TriangleLine } from '../edit_after_photo/shapes'
import { useFurnitureRemovalDrawingEvents } from './useFurnitureRemovalDrawingEvents'

/**
 * 家具部分消去用の描画コンポーネント
 * Konvaを使用してキャンバス上でマスク描画機能を提供
 */
const FurnitureRemovalDrawing = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const stageWrapRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<any>(null)

  const [stageWidth, setStageWidth] = useState(0)
  const [stageHeight, setStageHeight] = useState(0)
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)

  const {
    furnitureMaskLines,
    tempFurnitureMaskLines,
    furnitureBrushHead,
    furnitureBrushSize,
    furnitureRemovalOriginalImageUrl,
    showFurnitureRemovalDrawing,
  } = useSelector((state: any) => state.itemRemoval)

  // 描画イベントフック
  const { handleMouseDown, handleMouseMove, handleMouseUp, showCursor, hideCursor } =
    useFurnitureRemovalDrawingEvents(stageWrapRef)

  // 画像の読み込み
  useEffect(() => {
    if (!furnitureRemovalOriginalImageUrl) return

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      setImageElement(img)
    }
    img.src = furnitureRemovalOriginalImageUrl
  }, [furnitureRemovalOriginalImageUrl])

  // ステージサイズの計算
  useEffect(() => {
    if (!stageWrapRef.current || !imageElement) return

    const maxStageHeight =
      window.innerHeight - 11 * parseInt(getComputedStyle(document.documentElement).fontSize)
    const maxStageWidth = stageWrapRef.current.clientWidth

    let newStageHeight = imageElement.height * (maxStageWidth / imageElement.width)
    let newStageWidth: number

    if (newStageHeight <= maxStageHeight) {
      newStageWidth = maxStageWidth
    } else {
      newStageHeight = maxStageHeight
      newStageWidth = imageElement.width * (maxStageHeight / imageElement.height)
    }

    setStageHeight(newStageHeight)
    setStageWidth(newStageWidth)
    stageWrapRef.current.style.height = newStageHeight + 'px'
  }, [imageElement])

  // stageRefをReduxに保存
  useEffect(() => {
    if (stageRef.current && showFurnitureRemovalDrawing) {
      const stage = stageRef.current.getStage()
      dispatch(itemRemovalActions.setFurnitureMaskStageRef(stage))
    }
  }, [stageRef.current, showFurnitureRemovalDrawing, dispatch])

  // 線を描画するヘルパー関数
  const renderLine = (line: FurnitureMaskLine, index: number, prefix: string, opacity: number) => {
    const color = `rgba(255, 0, 0, ${opacity})`

    if (line.head === 'circle') {
      return (
        <Line
          key={`${prefix}-${index}`}
          points={line.points}
          stroke={color}
          strokeWidth={line.headSize}
          tension={0.3}
          lineCap="round"
          lineJoin="round"
        />
      )
    }
    if (line.head === 'square') {
      return (
        <SquareLine
          key={`${prefix}-${index}`}
          line={line}
          stroke={color}
          fill={color}
          opacity={1}
        />
      )
    }
    if (line.head === 'triangle') {
      return (
        <TriangleLine
          key={`${prefix}-${index}`}
          line={line}
          stroke={color}
          fill={color}
          opacity={1}
        />
      )
    }
    return null
  }

  return (
    <div ref={stageWrapRef} className="furniture-removal-drawing-wrapper relative flex justify-center">
      <Stage
        ref={stageRef}
        width={stageWidth}
        height={stageHeight}
        className="mx-auto"
        style={{ cursor: cursorStyle(furnitureBrushHead, furnitureBrushSize) }}
        onMouseEnter={showCursor}
        onMouseLeave={hideCursor}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {/* 背景画像レイヤー */}
        <Layer>
          {imageElement && (
            <KonvaImage
              image={imageElement}
              width={stageWidth}
              height={stageHeight}
            />
          )}
        </Layer>

        {/* マスク描画レイヤー */}
        <Layer listening={false}>
          {/* 確定済みのマスク線（不透明度0.6） */}
          {furnitureMaskLines.map((line: FurnitureMaskLine, i: number) =>
            renderLine(line, i, 'confirmed', 0.6)
          )}

          {/* 描画中の一時的なマスク線（不透明度0.3） */}
          {tempFurnitureMaskLines.map((line: FurnitureMaskLine, i: number) =>
            renderLine(line, i, 'temp', 0.3)
          )}
        </Layer>
      </Stage>
    </div>
  )
}

export default FurnitureRemovalDrawing
